import type { DisplayScores, PortfolioRow } from '@/lib/api/endpoints/portfolio'

export const SORT_FIELDS = ['name', 'sector', 'quadrant', 'urgency', 'def', 'rdy'] as const

export type SortField = (typeof SORT_FIELDS)[number]
export type SortDirection = 'asc' | 'desc'

export interface SortState {
  field: SortField
  direction: SortDirection
}

export const DEFAULT_SORT: SortState = { field: 'urgency', direction: 'desc' }

const BY_SCORES: Record<
  'quadrant' | 'urgency' | 'def' | 'rdy',
  (a: DisplayScores, b: DisplayScores) => number
> = {
  quadrant: (a, b) => a.quadrant.localeCompare(b.quadrant),
  urgency: (a, b) => a.urgency - b.urgency,
  def: (a, b) => a.def - b.def,
  rdy: (a, b) => a.rdy - b.rdy,
}

const BY_ROW: Record<'name' | 'sector', (a: PortfolioRow, b: PortfolioRow) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  sector: (a, b) => (a.sector ?? '').localeCompare(b.sector ?? ''),
}

export function sortRows(rows: readonly PortfolioRow[], sort: SortState): PortfolioRow[] {
  const sign = sort.direction === 'asc' ? 1 : -1

  return [...rows].sort((a, b) => {
    const left = a.scores
    const right = b.scores

    if (!left && right) return 1
    if (left && !right) return -1
    if (!left || !right) return a.name.localeCompare(b.name)

    if (sort.field === 'name' || sort.field === 'sector') {
      return sign * BY_ROW[sort.field](a, b)
    }

    return sign * BY_SCORES[sort.field](left, right)
  })
}

export function nextSort(current: SortState, field: SortField): SortState {
  return field === current.field
    ? { field, direction: current.direction === 'desc' ? 'asc' : 'desc' }
    : { field, direction: 'desc' }
}
